import { useState, useEffect } from 'react';
import { dgiiApi } from '../../api/dgii';
import { CheckCircleIcon, DocumentTextIcon, EyeIcon, PhotoIcon, ClockIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon, ArrowPathIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function ProcessedInvoicesList({ onRefresh, empresaSeleccionada }) {
	const [formularios, setFormularios] = useState([]);
	const [archivosConError, setArchivosConError] = useState([]);
	const [loading, setLoading] = useState(false);
	const [reprocesando, setReprocesando] = useState({});
	const [eliminando, setEliminando] = useState({});
	const [selectedFormulario, setSelectedFormulario] = useState(null);
	const [showImageModal, setShowImageModal] = useState(false);
	const [imageUrl, setImageUrl] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage] = useState(10); // 10 facturas por página
	const [mostrarErrores, setMostrarErrores] = useState(true); // Por defecto mostrar errores cuando existen

	useEffect(() => {
		loadFormularios();
	}, [onRefresh, empresaSeleccionada]);

	// También recargar cuando el componente se monta
	useEffect(() => {
		loadFormularios();
	}, [empresaSeleccionada]);

	const loadFormularios = async () => {
		setLoading(true);
		try {
			const params = {};
			if (empresaSeleccionada) {
				params.empresa = empresaSeleccionada;
			}
			const data = await dgiiApi.getFormularios(params);
			const formulariosArray = Array.isArray(data) ? data : (data?.results || []);
			// Filtrar solo formularios 606 que tienen facturas procesadas
			const formulariosConFacturas = formulariosArray.filter(form => {
				// Solo formularios 606
				if (form.tipo !== '606') return false;
				
				const datosExtraidos = form.datos_extraidos || {};
				const datosCompletados = form.datos_completados || {};
				const compras = datosCompletados.compras || datosExtraidos.compras || [];
				return compras.length > 0;
			});
			setFormularios(formulariosConFacturas);
			
			// Cargar archivos con errores o pendientes
			await loadArchivosConErrores();
		} catch (err) {
			console.error('Error cargando formularios:', err);
			setFormularios([]);
		} finally {
			setLoading(false);
		}
	};
	
	const loadArchivosConErrores = async () => {
		try {
			const params = {};
			if (empresaSeleccionada) {
				params.empresa = empresaSeleccionada;
			}
			// Cargar TODOS los archivos (no solo los procesados) para ver errores y pendientes
			const data = await dgiiApi.getArchivosAdjuntos(params);
			const archivosArray = Array.isArray(data) ? data : (data?.results || []);
			
			// Filtrar archivos con error o pendientes
			// También incluir archivos que están en estado 'procesando' por más de 5 minutos (posible error)
			const ahora = new Date();
			const archivosError = archivosArray.filter(archivo => {
				const estado = archivo.estado_procesamiento;
				if (estado === 'error' || estado === 'pendiente') {
					return true;
				}
				// Si está procesando por más de 5 minutos, considerarlo como posible error
				if (estado === 'procesando' && archivo.fecha_subida) {
					const fechaSubida = new Date(archivo.fecha_subida);
					const minutosTranscurridos = (ahora - fechaSubida) / (1000 * 60);
					if (minutosTranscurridos > 5) {
						return true;
					}
				}
				return false;
			});
			
			setArchivosConError(archivosError);
			// Si hay archivos con error, mostrar la sección automáticamente
			if (archivosError.length > 0) {
				setMostrarErrores(true);
			}
		} catch (err) {
			console.error('Error cargando archivos con errores:', err);
			setArchivosConError([]);
		}
	};
	
	const handleReprocesar = async (archivoId) => {
		if (!window.confirm('¿Está seguro de que desea reprocesar este archivo?')) {
			return;
		}
		
		setReprocesando(prev => ({ ...prev, [archivoId]: true }));
		try {
			const result = await dgiiApi.reprocesarArchivo(archivoId, '606');

			// Recargar inmediatamente (el archivo pasa a 'pendiente')
			await loadArchivosConErrores();
			await loadFormularios();
			if (onRefresh && typeof onRefresh === 'function') {
				onRefresh();
			}

			// Si la respuesta es asíncrona (nueva lógica con Celery), recargar después de unos segundos
			if (result?.modo === 'asincrono') {
				setTimeout(async () => {
					await loadArchivosConErrores();
					await loadFormularios();
					if (onRefresh && typeof onRefresh === 'function') onRefresh();
				}, 15000); // Recargar a los 15 s para capturar el resultado
			}
		} catch (err) {
			console.error('Error reprocesando archivo:', err);
			
			// Mensaje de error más amigable
			let mensajeError = 'Error al reprocesar el archivo.';
			const errorDetail = err.response?.data?.detail || err.response?.data?.error || err.message || '';
			
			// Detectar diferentes tipos de errores y mostrar mensajes apropiados
			if (errorDetail.toLowerCase().includes('ocr') || 
			    errorDetail.toLowerCase().includes('vision') ||
			    errorDetail.toLowerCase().includes('texto') ||
			    errorDetail.toLowerCase().includes('imagen') ||
			    errorDetail.toLowerCase().includes('procesamiento')) {
				mensajeError = 'No se pudo procesar la imagen de la factura. Por favor, vuelva a cargar la factura con una imagen más clara y nítida.';
			} else if (errorDetail.toLowerCase().includes('empresa') || errorDetail.toLowerCase().includes('company')) {
				mensajeError = 'El archivo no tiene una empresa asignada. Por favor, asegúrese de seleccionar una empresa antes de procesar.';
			} else if (errorDetail.toLowerCase().includes('permiso') || errorDetail.toLowerCase().includes('permission')) {
				mensajeError = 'No tiene permisos para reprocesar este archivo.';
			} else if (errorDetail) {
				// Si hay un detalle específico pero no coincide con los casos anteriores, usarlo
				mensajeError = `Error: ${errorDetail}`;
			}
			
			alert(mensajeError);
		} finally {
			setReprocesando(prev => ({ ...prev, [archivoId]: false }));
		}
	};
	
	const handleEliminar = async (archivoId, nombreArchivo) => {
		if (!window.confirm(`¿Está seguro de que desea eliminar "${nombreArchivo}"?\n\nEsta acción eliminará el archivo y todos sus datos procesados. No se puede deshacer.`)) {
			return;
		}
		setEliminando(prev => ({ ...prev, [archivoId]: true }));
		try {
			await dgiiApi.deleteFactura(archivoId);
			await loadArchivosConErrores();
			if (onRefresh && typeof onRefresh === 'function') {
				onRefresh();
			}
		} catch (err) {
			console.error('Error eliminando archivo:', err);
			alert('Error al eliminar el archivo: ' + (err.response?.data?.detail || err.message));
		} finally {
			setEliminando(prev => ({ ...prev, [archivoId]: false }));
		}
	};

	const getEstadoBadge = (estado) => {
		const estados = {
			'completado': { color: 'bg-green-100 text-green-800', icon: CheckCircleIcon, texto: 'Completado' },
			'error': { color: 'bg-red-100 text-red-800', icon: XMarkIcon, texto: 'Error' },
			'pendiente': { color: 'bg-yellow-100 text-yellow-800', icon: ClockIcon, texto: 'Pendiente' },
			'procesando': { color: 'bg-blue-100 text-blue-800', icon: ClockIcon, texto: 'Procesando' },
		};
		return estados[estado] || estados['pendiente'];
	};

	const getFacturasFromFormulario = (formulario) => {
		// PRIORIDAD 1: Obtener facturas de FacturaExtraida (más confiable)
		// Esto cuenta todas las facturas extraídas, no solo las que están en datos_extraidos
		let facturasDeFacturaExtraida = [];
		if (formulario.archivos_adjuntos) {
			formulario.archivos_adjuntos.forEach(archivo => {
				if (archivo.facturas_extraidas && Array.isArray(archivo.facturas_extraidas)) {
					const facturasDelFormulario = archivo.facturas_extraidas.filter(factura => {
						const facturaFormId = typeof factura.formulario === 'object' 
							? factura.formulario?.id 
							: factura.formulario;
						return facturaFormId === formulario.id;
					});
					facturasDeFacturaExtraida = facturasDeFacturaExtraida.concat(
						facturasDelFormulario.map(f => f.datos_estructurados || {})
					);
				}
			});
		}
		
		// PRIORIDAD 2: Si no hay facturas en FacturaExtraida, usar datos_extraidos/datos_completados
		if (facturasDeFacturaExtraida.length > 0) {
			return facturasDeFacturaExtraida;
		}
		
		const datosExtraidos = formulario.datos_extraidos || {};
		const datosCompletados = formulario.datos_completados || {};
		
		// Solo formulario 606 - siempre retornar compras
		const comprasCompletadas = datosCompletados.compras || [];
		const comprasExtraidas = datosExtraidos.compras || [];
		return comprasCompletadas.length > 0 ? comprasCompletadas : comprasExtraidas;
	};

	const getImageUrlForFactura = (factura, formulario) => {
		// Intentar encontrar el archivo adjunto relacionado
		if (formulario.archivos_adjuntos && formulario.archivos_adjuntos.length > 0) {
			// Buscar en los archivos adjuntos
			for (const archivo of formulario.archivos_adjuntos) {
				if (archivo.facturas_extraidas && archivo.facturas_extraidas.length > 0) {
					const facturaEncontrada = archivo.facturas_extraidas.find(
						f => f.ncf === factura.ncf || f.rnc_emisor === factura.rnc_emisor
					);
					if (facturaEncontrada && archivo.archivo) {
						return archivo.archivo;
					}
				}
			}
		}
		return null;
	};

	const viewImage = (url) => {
		setImageUrl(url);
		setShowImageModal(true);
	};

	const formatCurrency = (value) => {
		if (value === null || value === undefined) return 'RD$0.00';
		return new Intl.NumberFormat('es-DO', {
			style: 'currency',
			currency: 'DOP',
			minimumFractionDigits: 2
		}).format(value);
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('es-DO', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			});
		} catch {
			return dateString;
		}
	};

	const getTotalFacturas = () => {
		return formularios.reduce((total, form) => {
			return total + getFacturasFromFormulario(form).length;
		}, 0);
	};

	// Obtener todas las facturas de todos los formularios con información del formulario
	const getAllFacturas = () => {
		const allFacturas = [];
		formularios.forEach(formulario => {
			const facturas = getFacturasFromFormulario(formulario);
			facturas.forEach(factura => {
				allFacturas.push({
					...factura,
					formulario: formulario // Incluir referencia al formulario
				});
			});
		});
		return allFacturas;
	};

	// Calcular facturas paginadas
	const allFacturas = getAllFacturas();
	const totalFacturas = allFacturas.length;
	const totalPages = Math.ceil(totalFacturas / itemsPerPage);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const facturasPaginadas = allFacturas.slice(startIndex, endIndex);

	// Resetear a página 1 cuando cambian los formularios
	useEffect(() => {
		setCurrentPage(1);
	}, [formularios.length]);

	const handlePreviousPage = () => {
		setCurrentPage(prev => Math.max(1, prev - 1));
	};

	const handleNextPage = () => {
		setCurrentPage(prev => Math.min(totalPages, prev + 1));
	};

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	if (loading) {
		return (
			<div className="bg-white rounded-lg shadow-md p-6">
				<div className="flex items-center justify-center py-8">
					<ClockIcon className="h-8 w-8 animate-spin text-primary" />
					<span className="ml-2 text-gray-600">Cargando facturas procesadas...</span>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center space-x-2">
					<CheckCircleIcon className="h-6 w-6 text-green-600" />
					<h3 className="text-lg sm:text-xl font-bold text-gray-800">
						Facturas Procesadas
					</h3>
					<span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
						{totalFacturas}
					</span>
					{archivosConError.length > 0 && (
						<button
							onClick={() => setMostrarErrores(!mostrarErrores)}
							className={`ml-2 flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-full transition-colors ${
								mostrarErrores 
									? 'bg-red-600 text-white hover:bg-red-700' 
									: 'bg-red-100 text-red-800 hover:bg-red-200'
							}`}
						>
							<ExclamationTriangleIcon className="h-4 w-4" />
							<span>{archivosConError.length} con error{archivosConError.length !== 1 ? 'es' : ''}</span>
						</button>
					)}
				</div>
			</div>

			{/* Resumen */}
			<div className="mb-4 p-3 bg-gray-50 rounded-lg">
				<p className="text-sm text-gray-700">
					<strong>Total de facturas procesadas:</strong> {totalFacturas} en {formularios.length} formulario(s)
					{archivosConError.length > 0 && (
						<span className="ml-2 text-red-600">
							• <strong>{archivosConError.length}</strong> archivo(s) con error o pendiente(s)
						</span>
					)}
				</p>
			</div>
			
			{/* Sección de archivos con errores - Mostrar siempre si hay errores */}
			{archivosConError.length > 0 && (
				<div className={`mb-4 p-4 border rounded-lg transition-all ${
					mostrarErrores 
						? 'bg-red-50 border-red-200' 
						: 'bg-yellow-50 border-yellow-200'
				}`}>
					<div className="flex items-center justify-between mb-3">
						<h4 className={`text-sm font-semibold flex items-center space-x-2 ${
							mostrarErrores ? 'text-red-800' : 'text-yellow-800'
						}`}>
							<ExclamationTriangleIcon className="h-5 w-5" />
							<span>Archivos con Errores o Pendientes ({archivosConError.length})</span>
						</h4>
						<button
							onClick={() => setMostrarErrores(!mostrarErrores)}
							className={`${mostrarErrores ? 'text-red-600 hover:text-red-800' : 'text-yellow-600 hover:text-yellow-800'}`}
							title={mostrarErrores ? 'Ocultar detalles' : 'Mostrar detalles'}
						>
							{mostrarErrores ? (
								<XMarkIcon className="h-5 w-5" />
							) : (
								<ExclamationTriangleIcon className="h-5 w-5" />
							)}
						</button>
					</div>
					{mostrarErrores && (
					<div className="space-y-2">
						{archivosConError.map((archivo) => {
							const estadoInfo = getEstadoBadge(archivo.estado_procesamiento);
							const EstadoIcon = estadoInfo.icon;
							return (
								<div key={archivo.id} className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
									<div className="flex-1">
										<div className="flex items-center space-x-2 mb-1">
											<EstadoIcon className={`h-4 w-4 ${estadoInfo.color.replace('bg-', 'text-').replace('-100', '-800')}`} />
											<span className="text-sm font-medium text-gray-900">{archivo.nombre_original || 'Sin nombre'}</span>
											<span className={`text-xs px-2 py-0.5 rounded-full ${estadoInfo.color}`}>
												{estadoInfo.texto}
											</span>
										</div>
										{archivo.errores && (
											<p className="text-xs text-red-600 mt-1">{archivo.errores}</p>
										)}
										{archivo.fecha_subida && (
											<p className="text-xs text-gray-500 mt-1">
												Subido: {formatDate(archivo.fecha_subida)}
											</p>
										)}
									</div>
								<div className="ml-4 flex items-center gap-2 flex-shrink-0">
									<button
										onClick={() => handleReprocesar(archivo.id)}
										disabled={reprocesando[archivo.id] || eliminando[archivo.id]}
										className="px-3 py-1.5 text-xs font-medium text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
									>
										{reprocesando[archivo.id] ? (
											<>
												<ClockIcon className="h-4 w-4 animate-spin" />
												<span>Reprocesando...</span>
											</>
										) : (
											<>
												<ArrowPathIcon className="h-4 w-4" />
												<span>Reprocesar</span>
											</>
										)}
									</button>
									<button
										onClick={() => handleEliminar(archivo.id, archivo.nombre_original || 'archivo')}
										disabled={reprocesando[archivo.id] || eliminando[archivo.id]}
										className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
										title="Eliminar archivo"
									>
										{eliminando[archivo.id] ? (
											<>
												<ClockIcon className="h-4 w-4 animate-spin" />
												<span>Eliminando...</span>
											</>
										) : (
											<>
												<TrashIcon className="h-4 w-4" />
												<span>Eliminar</span>
											</>
										)}
									</button>
								</div>
								</div>
							);
						})}
					</div>
					)}
				</div>
			)}

			{formularios.length === 0 ? (
				<div className="text-center py-8 text-gray-500">
					<DocumentTextIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
					<p>No hay facturas procesadas</p>
				</div>
			) : (
				<>
					{/* Tabla de facturas */}
					<div className="overflow-x-auto mb-4">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Formulario</th>
									<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">NCF</th>
									<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">RNC</th>
									<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
									<th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
									<th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{facturasPaginadas.map((factura, idx) => (
									<tr key={idx} className="hover:bg-gray-50">
										<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
											{factura.formulario.tipo} - {factura.formulario.periodo}
										</td>
										<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
											{factura.ncf || 'N/A'}
										</td>
										<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
											{factura.rnc_emisor || factura.rnc_cedula || 'N/A'}
										</td>
										<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
											{formatDate(factura.fecha_comprobante || factura.fecha)}
										</td>
										<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 font-medium">
											{formatCurrency(factura.total_factura || factura.monto_facturado)}
										</td>
										<td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
											{getImageUrlForFactura(factura, factura.formulario) && (
												<button
													onClick={() => viewImage(getImageUrlForFactura(factura, factura.formulario))}
													className="text-primary hover:text-primary/80 p-1"
													title="Ver imagen"
												>
													<EyeIcon className="h-5 w-5" />
												</button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* Paginación */}
					{totalPages > 1 && (
						<div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
							<div className="flex flex-1 justify-between sm:hidden">
								<button
									onClick={handlePreviousPage}
									disabled={currentPage === 1}
									className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
										currentPage === 1
											? 'text-gray-400 cursor-not-allowed'
											: 'text-gray-700 hover:bg-gray-50'
									}`}
								>
									Anterior
								</button>
								<button
									onClick={handleNextPage}
									disabled={currentPage === totalPages}
									className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium ${
										currentPage === totalPages
											? 'text-gray-400 cursor-not-allowed'
											: 'text-gray-700 hover:bg-gray-50'
									}`}
								>
									Siguiente
								</button>
							</div>
							<div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
								<div>
									<p className="text-sm text-gray-700">
										Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
										<span className="font-medium">{Math.min(endIndex, totalFacturas)}</span> de{' '}
										<span className="font-medium">{totalFacturas}</span> facturas
									</p>
								</div>
								<div>
									<nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
										<button
											onClick={handlePreviousPage}
											disabled={currentPage === 1}
											className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
												currentPage === 1 ? 'cursor-not-allowed opacity-50' : ''
											}`}
										>
											<span className="sr-only">Anterior</span>
											<ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
										</button>
										{/* Números de página */}
										{Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
											// Mostrar solo algunas páginas alrededor de la actual
											if (
												page === 1 ||
												page === totalPages ||
												(page >= currentPage - 1 && page <= currentPage + 1)
											) {
												return (
													<button
														key={page}
														onClick={() => handlePageChange(page)}
														className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
															page === currentPage
																? 'z-10 bg-primary text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'
																: 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
														}`}
													>
														{page}
													</button>
												);
											} else if (page === currentPage - 2 || page === currentPage + 2) {
												return (
													<span
														key={page}
														className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300"
													>
														...
													</span>
												);
											}
											return null;
										})}
										<button
											onClick={handleNextPage}
											disabled={currentPage === totalPages}
											className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 ${
												currentPage === totalPages ? 'cursor-not-allowed opacity-50' : ''
											}`}
										>
											<span className="sr-only">Siguiente</span>
											<ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
										</button>
									</nav>
								</div>
							</div>
						</div>
					)}
				</>
			)}

			{/* Modal de imagen */}
			{showImageModal && imageUrl && (
				<div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
					<div className="relative max-w-4xl max-h-full">
						<button
							onClick={() => setShowImageModal(false)}
							className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
						>
							<XMarkIcon className="h-6 w-6" />
						</button>
						<img
							src={imageUrl}
							alt="Factura"
							className="max-w-full max-h-[90vh] object-contain rounded-lg"
						/>
					</div>
				</div>
			)}
		</div>
	);
}

